import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  getFreshSupabaseSession,
  syncSupabaseUserToBackend,
  abandonSupabaseLogin,
  markGoogleAuthFailed,
  extractLoginQuestionnaireFieldsFromEnvelope,
  persistLoginQuestionnaireFields,
} from '../services/auth';
import { toast } from 'react-toastify';
import { fetchMyProfile } from '../services/profile';
import { setProfile } from '../store/userSlice';
import { getCookie } from '../utils/helpers';
import { getPostAuthHomeOrQuestionnaire } from '../utils/signInRedirect';

export default function AuthCallback() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ranRef = useRef(false);
  const [loadingMessage, setLoadingMessage] = useState(
    'Authenticating with Google...'
  );

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const failAuthentication = async (message) => {
      // A stale/invalid Supabase token or a failed backend sync must not leave
      // the persisted Supabase session in place: SignIn keeps forwarding back
      // here, trapping the user in a /signin <-> /auth/callback loop. Clear the
      // Supabase session + partial cookies and return to a clean sign-in form.
      markGoogleAuthFailed();
      await abandonSupabaseLogin();
      try {
        toast.error(message, { toastId: 'google-signin-failed' });
      } catch (_) {
        // ignore toast errors
      }
      navigate('/signin', { replace: true });
    };

    const run = async () => {
      try {
        setLoadingMessage('Authenticating with Google...');
        // Refresh the Supabase token if it has expired before trusting it.
        // Forwarding an expired token to the backend returns 401 and is the
        // root cause of the sign-in loop.
        const session = await getFreshSupabaseSession();

        if (!session) {
          await failAuthentication(
            'Your Google sign-in has expired. Please sign in again.'
          );
          return;
        }

        setLoadingMessage('Verifying your account...');
        let backendData = null;
        try {
          backendData = await syncSupabaseUserToBackend(session);
        } catch (e) {
          await failAuthentication(
            'We could not complete your Google sign-in. Please try again.'
          );
          return;
        }

        // The backend sets the app session cookie on success. If it is missing,
        // authentication did not actually succeed — fail cleanly instead of
        // navigating into a PrivateRoute that bounces back to /signin.
        if (!getCookie('access')) {
          await failAuthentication(
            'We could not complete your Google sign-in. Please try again.'
          );
          return;
        }

        setLoadingMessage('Setting up your profile...');
        const user = session.user;
        const fullName =
          user?.user_metadata?.full_name || user?.user_metadata?.name || '';

        // Extract role and other data from backend response
        const userRole = backendData?.role || localStorage.getItem('userRole') || 'Patient';
        const userGender = backendData?.gender || '';
        const userDateOfBirth = backendData?.date_of_birth || null;
        const userOrganization = backendData?.organization || 'Biowell';
        const onBoardingCompleted = backendData?.on_boarding_completed;
        const onBoardingStep = backendData?.on_boarding_step;
        const mustChangePassword = Boolean(backendData?.must_change_password);

        let qFields = extractLoginQuestionnaireFieldsFromEnvelope(backendData || {});
        try {
          const profile = await fetchMyProfile();
          if (typeof profile.medical_questionnaire_completed === 'boolean') {
            qFields = {
              ...qFields,
              medical_questionnaire_completed: profile.medical_questionnaire_completed,
            };
          }
          if (
            profile.number_of_questions_filled !== null &&
            profile.number_of_questions_filled !== undefined &&
            Number.isFinite(Number(profile.number_of_questions_filled))
          ) {
            qFields = {
              ...qFields,
              number_of_questions_filled: Number(profile.number_of_questions_filled),
            };
          }
          if (profile.questionnaire_completion_percentage != null) {
            qFields = {
              ...qFields,
              questionnaire_completion_percentage: profile.questionnaire_completion_percentage,
            };
          }
          if (profile.sections_completed != null) {
            qFields = { ...qFields, sections_completed: profile.sections_completed };
          }
          if (profile.section_progress != null) {
            qFields = { ...qFields, section_progress: profile.section_progress };
          }
          if (profile.totalSections != null) {
            qFields = { ...qFields, total_sections: profile.totalSections };
          }
        } catch (_) {
          // ignore profile/me errors
        }
        persistLoginQuestionnaireFields(qFields);

        dispatch(
          setProfile({
            fullName,
            email: user?.email || '',
            gender: userGender,
            dateOfBirth: userDateOfBirth,
            role: userRole,
            organization: userOrganization,
            on_boarding_completed: onBoardingCompleted,
            on_boarding_step: onBoardingStep,
            must_change_password: mustChangePassword,
            ...(typeof qFields.medical_questionnaire_completed === 'boolean'
              ? { medical_questionnaire_completed: qFields.medical_questionnaire_completed }
              : {}),
            ...(qFields.number_of_questions_filled !== null &&
            qFields.number_of_questions_filled !== undefined &&
            Number.isFinite(Number(qFields.number_of_questions_filled))
              ? { number_of_questions_filled: Number(qFields.number_of_questions_filled) }
              : {}),
            ...(qFields.questionnaire_completion_percentage != null
              ? { questionnaire_completion_percentage: qFields.questionnaire_completion_percentage }
              : {}),
            ...(qFields.sections_completed != null &&
            Number.isFinite(Number(qFields.sections_completed))
              ? { sections_completed: Number(qFields.sections_completed) }
              : {}),
            ...(qFields.section_progress != null && Number.isFinite(Number(qFields.section_progress))
              ? { section_progress: Number(qFields.section_progress) }
              : {}),
            ...(qFields.total_sections != null && Number.isFinite(Number(qFields.total_sections))
              ? { totalSections: Number(qFields.total_sections) }
              : {}),
          })
        );

        try {
          localStorage.setItem('userFullName', fullName || '');
          localStorage.setItem('userEmail', user?.email || '');
          localStorage.setItem('userRole', userRole);
          localStorage.setItem('userGender', userGender);
          localStorage.setItem('userOrganization', userOrganization);
          if (userDateOfBirth) {
            localStorage.setItem('userDOB', userDateOfBirth);
          }
          if (typeof onBoardingCompleted === 'boolean') {
            localStorage.setItem('on_boarding_completed', String(onBoardingCompleted));
          }
          if (onBoardingStep != null && onBoardingStep !== '') {
            localStorage.setItem('on_boarding_step', String(onBoardingStep));
          }
          localStorage.setItem(
            'must_change_password',
            String(Boolean(mustChangePassword))
          );
        } catch (_) {
          // ignore localStorage errors
        }

        setLoadingMessage('Finalizing authentication...');

        const needsPersonalDetails =
          onBoardingCompleted === false &&
          (onBoardingStep === 'Personal Details' || String(onBoardingStep).trim() === 'Personal Details');
        const needsBarcodeActivation =
          onBoardingCompleted === false &&
          ['bar code', 'barcode'].includes(String(onBoardingStep || '').trim().toLowerCase());
        const onboardingIncomplete = onBoardingCompleted === false;
        const needsPasswordChange =
          onboardingIncomplete && mustChangePassword === true;
        // const needsMedicalInfo =
        //   onBoardingCompleted === false &&
        //   (onBoardingStep === 'Medical Info' || String(onBoardingStep).trim() === 'Medical Info');

        const getRedirectPath = () => {
          if (needsPasswordChange) return '/onboarding/change-password';
          if (needsBarcodeActivation) return '/activate-barcode';
          if (needsPersonalDetails) return '/personal-information';
          // if (needsMedicalInfo) return '/medical-info';
          let viewAs = null;
          try {
            viewAs = localStorage.getItem('supervisorViewAsRole');
          } catch (_) {
            // ignore
          }
          const role = userRole || localStorage.getItem('userRole') || 'Patient';
          return getPostAuthHomeOrQuestionnaire(
            qFields.medical_questionnaire_completed,
            role,
            viewAs
          );
        };

        setLoadingMessage('Success! Redirecting...');
        setTimeout(() => {
          navigate(getRedirectPath(), { replace: true });
        }, 500);
      } catch (error) {
        if (getCookie('access')) {
          // Already authenticated but a later step threw; hand off to /signin,
          // which forwards on to the app using the existing session.
          navigate('/signin', { replace: true });
        } else {
          await failAuthentication(
            'Something went wrong during sign-in. Please try again.'
          );
        }
      }
    };

    run();
  }, [dispatch, navigate]);

  return (
    <div className="min-w-screen min-h-screen flex items-center justify-center bg-[#F9F5F2]">
      <div className="flex flex-col items-center justify-center gap-6">
        <div className="w-16 h-16 rounded-full border-4 border-[#119975]/30 border-t-[#119975] animate-spin" />
        <div className="flex flex-col items-center gap-2">
          <span className="text-[#2B3133] font-poppins text-lg font-medium">
            {loadingMessage}
          </span>
          <span className="text-gray-500 font-poppins text-sm">
            Please wait...
          </span>
        </div>
      </div>
    </div>
  );
}
